import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, FileText, Book, ChatCircle, Sparkle, Feather, Fire, ChartLine, Eye, Download, DiscordLogo, Trophy, Warning, Article, Tag, Clock, CheckCircle, ArrowUp } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar, CartesianGrid, PieChart, Pie, Cell, Legend } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const STAT_CARDS = [
  { key: 'total_authors', label: 'Total Authors', icon: Feather, color: 'text-amber-600 bg-amber-100 dark:bg-amber-950/30' },
  { key: 'legendary_authors', label: 'Legendary Authors', icon: Sparkle, color: 'text-purple-600 bg-purple-100 dark:bg-purple-950/30' },
  { key: 'community_writers', label: 'Community Writers', icon: Users, color: 'text-blue-600 bg-blue-100 dark:bg-blue-950/30' },
  { key: 'total_works', label: 'Total Works', icon: Book, color: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-950/30' },
  { key: 'total_books', label: 'Total Books', icon: Book, color: 'text-indigo-600 bg-indigo-100 dark:bg-indigo-950/30' },
  { key: 'total_poems', label: 'Total Poems', icon: Feather, color: 'text-rose-600 bg-rose-100 dark:bg-rose-950/30' },
  { key: 'total_stories', label: 'Total Stories', icon: FileText, color: 'text-teal-600 bg-teal-100 dark:bg-teal-950/30' },
  { key: 'total_quotes', label: 'Total Quotes', icon: ChatCircle, color: 'text-orange-600 bg-orange-100 dark:bg-orange-950/30' },
  { key: 'total_articles', label: 'Total Articles', icon: Article, color: 'text-cyan-600 bg-cyan-100 dark:bg-cyan-950/30' },
  { key: 'total_categories', label: 'Categories', icon: Tag, color: 'text-lime-600 bg-lime-100 dark:bg-lime-950/30' },
  { key: 'total_users', label: 'Total Users', icon: Users, color: 'text-sky-600 bg-sky-100 dark:bg-sky-950/30' },
  { key: 'active_users_7d', label: 'Active (7d)', icon: Fire, color: 'text-red-600 bg-red-100 dark:bg-red-950/30' },
  { key: 'published_today', label: 'Published Today', icon: CheckCircle, color: 'text-green-600 bg-green-100 dark:bg-green-950/30' },
  { key: 'pending_reviews', label: 'Pending Reviews', icon: Clock, color: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-950/30' },
  { key: 'draft_works', label: 'Draft Works', icon: FileText, color: 'text-slate-600 bg-slate-100 dark:bg-slate-950/30' },
  { key: 'featured_content', label: 'Featured Content', icon: Trophy, color: 'text-amber-700 bg-amber-100 dark:bg-amber-950/30' },
  { key: 'total_reads', label: 'Total Reads', icon: Eye, color: 'text-fuchsia-600 bg-fuchsia-100 dark:bg-fuchsia-950/30' },
  { key: 'total_downloads', label: 'Downloads', icon: Download, color: 'text-violet-600 bg-violet-100 dark:bg-violet-950/30' },
  { key: 'storage_used_mb', label: 'Storage (MB)', icon: DiscordLogo, color: 'text-neutral-600 bg-neutral-100 dark:bg-neutral-800/50' },
];

const PIE_COLORS = ['#b0552b', '#d97757', '#e8a87c', '#c8b787', '#8a7563', '#5d8a68', '#6a89a7', '#a86e6e', '#7c6f9f', '#c98474'];

function StatCard({ label, value, Icon, color }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-sm transition-shadow" data-testid={`stat-card-${label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground truncate">{label}</p>
          <p className="text-2xl font-bold mt-1 truncate" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{value ?? '—'}</p>
        </div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color} flex-shrink-0`}>
          <Icon size={18} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="mb-4">
        <h3 className="text-lg font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      <div className="h-56 min-h-[220px]" style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

export default function CmsDashboard() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          axios.get(`${API}/api/admin/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/api/admin/dashboard/charts`, { withCredentials: true }),
        ]);
        setStats(s.data.totals);
        setCharts(c.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4" data-testid="cms-dashboard-loading">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {[...Array(19)].map((_, i) => <div key={i} className="h-24 bg-muted rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="cms-dashboard">
      {/* Stat Cards Grid */}
      <div>
        <h2 className="text-2xl mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>Overview</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="stats-grid">
          {STAT_CARDS.map((s) => (
            <StatCard key={s.key} label={s.label} value={stats?.[s.key]} Icon={s.icon} color={s.color} />
          ))}
        </div>
      </div>

      {/* Charts */}
      <div>
        <h2 className="text-2xl mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>Trends &amp; Insights</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Daily Visits" subtitle="Last 30 days">
            <ResponsiveContainer>
              <LineChart data={charts?.daily_visits || []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8 }} />
                <Line type="monotone" dataKey="visits" stroke="#b0552b" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Monthly Visits" subtitle="Last 12 months">
            <ResponsiveContainer>
              <BarChart data={charts?.monthly_visits || []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="visits" fill="#b0552b" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Top Authors" subtitle="By total work views">
            <ResponsiveContainer>
              <BarChart data={(charts?.top_authors || []).slice(0, 8)} layout="vertical" margin={{ left: 40 }}>
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                <Tooltip />
                <Bar dataKey="views" fill="#d97757" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Top Categories" subtitle="Works by type">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={charts?.top_categories || []}
                  dataKey="count"
                  nameKey="category"
                  outerRadius={70}
                  label={(e) => e.category}
                >
                  {(charts?.top_categories || []).map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="User Registrations" subtitle="Last 30 days">
            <ResponsiveContainer>
              <LineChart data={charts?.user_registrations || []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#5d8a68" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Top Works" subtitle="By reads">
            <div className="space-y-2 overflow-y-auto h-full">
              {(charts?.top_works || []).slice(0, 8).map((w, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-border last:border-0">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{w.title}</p>
                    <p className="text-xs text-muted-foreground capitalize">{w.type}</p>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0 ml-2">
                    <Eye size={12} /> {w.views}
                  </div>
                </div>
              ))}
              {(!charts?.top_works || charts.top_works.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-8">No works with views yet.</p>
              )}
            </div>
          </ChartCard>
        </div>
      </div>
    </div>
  );
}
